import {DocumentNode, useLazyQuery, useMutation} from '@apollo/client';
import {itemDataMutations, itemDataQueries, uploadMutations} from 'api/graphql-queries';
import {useAlert} from 'hooks/useAlert';
import {useCursorPaginationQuery} from 'hooks/useCommon';
import {ResponseViewModel} from 'models';
import {
    itemDataByOffsetPaginationResponse,
    ItemDataCreateRequest,
    ItemDataCreateResponse,
    ItemDataCursorPaginationResponse,
    ItemDataFieldModel,
    ItemDataGetAllByOffsetRequest,
    ItemDataGetAllCursoPaginationRequest,
    ItemDataModel,
    ItemDataResponse,
    ItemDataUpdateOrderAndStatuesResponse,
    ItemDataUpdateRequest,
    ItemDataWithNoPaginationResponse,
    ItemDateGetAllWithoutPaginationRequest,
    TransferItemsRequest,
    UpdateItemDataOrderAndStatusesRequest,
    UpdateItemDataSprintRequest,
    UpdateItemDataStatesRequest,
} from 'models/board/itemData';
import {useCallback, useEffect, useState} from 'react';

export const useRemoveItemDatasByIds = () => {
    const {invokeAlert, invokeSuccessMessage} = useAlert();
    const [remove, {data, loading, error}] = useMutation(itemDataMutations.REMOVE_ITEM_DATAS_BY_IDS);

    const removeItemDatasById = useCallback(async (ids: string[]) => {
        remove({
            variables: {ids},
        })
            .then(() => {
                invokeSuccessMessage('Item removed successfully');
            })
            .catch(e => {
                invokeAlert(error?.message ?? 'Failed to remove item ');
            });
    }, []);

    return {
        removeItemDatasById,
        removeItemDatasByIdLoading: loading,
        removeItemDatasByIdResponse: data,
    };
};

export const useUpdateItemDataAPI = () => {
    const {invokeAlert, invokeSuccessMessage} = useAlert();
    const [state, setState] = useState<ItemDataResponse>();
    const [update, {loading, data}] = useMutation<{updateItemData: ItemDataModel}, {input: ItemDataUpdateRequest}>(
        itemDataMutations.UPDATE_ITEM_DATA,
    );

    const updateItemData = useCallback(
        (input: ItemDataUpdateRequest) => {
            update({
                variables: {
                    input: {
                        ...input,
                        data: input.data,
                        sprintId: input.sprintId !== '' ? input.sprintId : undefined,
                    },
                },
            }).catch(error => {
                invokeAlert(error?.message ?? 'Failed to update item');
            });
        },
        [update],
    );
    useEffect(() => {
        if (data) {
            invokeSuccessMessage("Item's data updated successfully");
            setState({
                ...data.updateItemData,
                data: data.updateItemData.data,
                active: data.updateItemData.active ?? false,
            });
        }
    }, [data]);

    return {
        updateItemData,
        updateItemDataLoading: loading,
        updateItemDataResponse: state,
    };
};
export const useGetAllITemDataCursorPagination = (query: DocumentNode = itemDataQueries.GET_ALL_CURSOR_PAGINATION) => {
    const {isLoading, fetchPaginated, upsertItemState, data} = useCursorPaginationQuery<
        ItemDataResponse, // TItem
        ItemDataCursorPaginationResponse, // TResponse
        ItemDataGetAllCursoPaginationRequest, // TVariables
        'itemDatasWithCursorPagination' // TKey (key inside response)
    >(query, 'itemDatasWithCursorPagination', 'no-cache');
    return {
        getAllITemDataCursorPaginationLoading: isLoading,
        getAllITemDataCursorPaginationResponse: data,
        getAllITemDataCursorPagination: fetchPaginated,
        afterCreateUpdateHandler: upsertItemState,
    };
};

export const useGetItemById = () => {
    const [refetch, {loading, data}] = useLazyQuery<{oneItemData: ItemDataResponse}, {id: string}>(
        itemDataQueries.GET_ITEM_BY_ID,
    );
    return {
        getItemById: refetch,
        getItemByIdLoading: loading,
        getItemByIdResponse: data?.oneItemData,
    };
};
export const useGetItemDatasByOffsetPagination = () => {
    const [refetch, {loading, data}] = useLazyQuery<itemDataByOffsetPaginationResponse, ItemDataGetAllByOffsetRequest>(
        itemDataQueries.GET_ITEM_DATAS_BY_OFFSET_PAGINATION,
        {
            fetchPolicy: 'network-only',
        },
    );

    return {
        getAllitemDataLoading: loading,
        getAllitemDataResponse: data?.itemDatasWithOffsetPagination,
        getAllitemData: refetch,
    };
};

export const useGetAllItemDataWithOtherPagiantion = () => {
    const [refetch, {loading, data}] = useLazyQuery<
        ItemDataWithNoPaginationResponse,
        ItemDateGetAllWithoutPaginationRequest
    >(itemDataQueries.GET_ALL_ITEM_DATA_WITH_OUT_PAGINATION, {
        fetchPolicy: 'network-only',
    });

    return {
        getAllitemDataWithOtherPaginationLoading: loading,
        getAllitemDataWithOtherPaginationResponse: data?.itemDatas,
        getAllitemDataWithOtherPagination: refetch,
    };
};

export const useCreateItemDataAPI = () => {
    const [create, {data, loading}] = useMutation<ItemDataCreateResponse, {input: ItemDataCreateRequest}>(
        itemDataMutations.CREATE_ITEM_DATA,
    );

    return {
        createItemData: create,
        createItemDataLaoding: loading,
        createItemDataResponse: data,
    };
};

export const useUpdateItemsOrderAndStatuses = () => {
    const {invokeAlert} = useAlert();
    const [mutate, {data, loading, error}] = useMutation<
        {updateItemsOrderAndStatuses: ResponseViewModel<ItemDataUpdateOrderAndStatuesResponse>},
        {input: UpdateItemDataOrderAndStatusesRequest[]; draggedItemId?: string | null}
    >(itemDataMutations.UPDATE_ITEM_DATA_ORDER_AND_STATUS);

    const updateItemsOrderAndStatuses = useCallback(
        (request: UpdateItemDataOrderAndStatusesRequest[], draggedItemId: string | null) => {
            mutate({
                variables: {input: request, draggedItemId: draggedItemId ?? undefined},
            }).catch(error => {
                invokeAlert(error?.message ?? 'Failed to create item');
            });
        },
        [mutate],
    );

    return {
        updateItemsOrderAndStatuses,
        updateItemsOrderAndStatusesLoading: loading,
        updateItemsOrderAndStatusesError: error,
        updateItemsOrderAndStatusesResponse: data?.updateItemsOrderAndStatuses,
    };
};

export const useGetItemDataById = () => {
    const [refetch, {loading, data}] = useLazyQuery<{oneItemData: ItemDataResponse}>(
        itemDataQueries.GET_ITEM_DATA_BY_TICKET_NO as DocumentNode,
        {
            fetchPolicy: 'no-cache', // Doesn't check cache before making a network request
        },
    );
    const getItemDataById = useCallback(
        (ticketNo: number, boardId: string) => {
            refetch({variables: {ticketNo: ticketNo, boardId: boardId}});
        },
        [refetch],
    );
    return {getItemDataByIdLoading: loading, getItemDataByIdResponse: data?.oneItemData, getItemDataById};
};

export const useMoveItemToStatus = (withOutAlear?: boolean) => {
    const {invokeSuccessMessage} = useAlert();
    const [refetch, {loading, data}] = useMutation<
        {updateItemStates: ItemDataModel[]},
        {input: UpdateItemDataStatesRequest}
    >(itemDataMutations.UPDATE_ITEM_STATUS_FOR_ITEM, {
        fetchPolicy: 'no-cache',
    });
    useEffect(() => {
        if (data && !loading) {
            if (!withOutAlear) {
                invokeSuccessMessage(`The status successfully updated.`);
            }
        }
    }, [data, loading]);
    return {
        moveItemToStatus: refetch,
        moveItemToStatusLoading: loading,
        moveItemToStatusResponse: data?.updateItemStates,
    };
};
export const useMoveItemToSprint = (withOutAlear?: boolean) => {
    const {invokeSuccessMessage} = useAlert();
    const [refetch, {loading, data}] = useMutation<
        {updateItemSprints: ItemDataModel[]},
        {input: UpdateItemDataSprintRequest}
    >(itemDataMutations.UPDATE_ITEM_SPRINT_FOR_ITEM, {
        fetchPolicy: 'no-cache',
    });
    useEffect(() => {
        if (data && !loading) {
            if (!withOutAlear) {
                invokeSuccessMessage(`The sprint successfully updated.`);
            }
        }
    }, [data, loading]);
    return {
        moveItemToSprint: refetch,
        moveItemToSprintLoading: loading,
        moveItemToSprintResponse: data?.updateItemSprints,
    };
};

export const useTransferItems = (withOutAlear?: boolean) => {
    const {invokeSuccessMessage} = useAlert();
    const [refetch, {loading, data}] = useMutation<{transferItems: ItemDataModel[]}, {input: TransferItemsRequest}>(
        itemDataMutations.TRANSFER_ITEMS,
        {
            fetchPolicy: 'no-cache',
        },
    );
    useEffect(() => {
        if (data && !loading) {
            if (!withOutAlear) {
                invokeSuccessMessage(`items successfully transfered.`);
            }
        }
    }, [data, loading]);
    return {
        transferItems: refetch,
        transferItemsLoading: loading,
        transferItemsResponse: data?.transferItems,
    };
};

export const useUploadItemDataForBoardAPI = () => {
    const {invokeSuccessMessage} = useAlert();
    const [uploadFile, {data, loading}] = useMutation(uploadMutations.UPLOAD_ITEM_DATA_FOR_BOARD);
    const uploadItemDataForBoard = useCallback(
        (
            file: File,
            itemFieldsFormat: ItemDataFieldModel[],
            statusId: string,
            uniqueField: string,
            sprintId?: string,
        ) => {
            uploadFile({
                variables: {file, itemFieldsFormat, statusId, uniqueField, sprintId},
                context: {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'GraphQL-preflight': '1',
                    },
                },
            });
        },
        [uploadFile],
    );
    useEffect(() => {
        if (data && !loading) {
            invokeSuccessMessage('Item data uploaded successfully');
        }
    }, [data, loading]);

    return {
        uploadItemDataForBoard,
        uploadItemDataForBoardResponse: data,
        uploadItemDataForBoardLoading: loading,
    };
};
